import { useQuery } from "@apollo/client";
import axios from "axios";
import Errors from "components/base/Errors";
import Icon from "components/base/Icon";
import ProgressRing from "components/base/ProgressRing";
import Spinner from "components/base/Spinner";
import gql from "graphql-tag";
import { useEffect, useState } from "react";

const FETCH_AWS_PRESIGNED_URL = gql`
  query FETCH_AWS_PRESIGNED_URL(
    $filename: String!
    $contentType: String
    $acl: String
    $dir: String
  ) {
    awsPreSignedUrl(
      filename: $filename
      contentType: $contentType
      acl: $acl
      dir: $dir
    ) {
      url
      fields
    }
  }
`;

function Uploader({
  className = "",
  awsPreSignedUrl,
  file,
  preview,
  ignoreUploadedState,
  onUploaded,
  onError,
}) {
  const [progress, setProgress] = useState(0);

  function uploadFile() {
    const config = {
      onUploadProgress: function (progressEvent) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        setProgress(percentCompleted);
      },
    };
    const http = axios.create(config);

    const fields = JSON.parse(awsPreSignedUrl.fields);

    let data = new FormData();
    for (let [key, value] of Object.entries(fields)) {
      data.append(key, value);
    }
    data.append("file", file);
    http
      .post(awsPreSignedUrl.url, data, config)
      .then(function () {
        onUploaded(`${awsPreSignedUrl.url}${fields.key}`);
      })
      .catch(function (err) {
        console.log(err);
        if (onError) onError(err);
      });
  }

  useEffect(uploadFile, [file]);

  const uploaded = progress === 100;

  return (
    <div className={`${className} relative`}>
      {preview ? <img className="h-32" src={preview} alt="preview" /> : null}
      <div
        className={`absolute inset-0 z-10 flex items-center justify-center
               ${preview ? (uploaded ? "bg-dark-20" : "bg-dark-50") : ""}
            `}
      >
        {!ignoreUploadedState && uploaded ? (
          <div className="text-green-500">
            <Icon icon="check" size={30} />
          </div>
        ) : (
          <div style={{ width: 40, height: 40 }}>
            <ProgressRing radius={20} stroke={2} progress={progress} />
          </div>
        )}
      </div>
    </div>
  );
}

function AWSUploader({
  file,
  preview,
  onUploaded,
  onError,
  ignoreUploadedState,
  className,
}) {
  const { loading, error, data } = useQuery(FETCH_AWS_PRESIGNED_URL, {
    variables: {
      filename: file.name,
      contentType: file.type,
      acl: "public-read",
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (error && onError) onError(error);
  }, [error, onError]);

  if (loading) return <Spinner text={null} />;
  if (error) return <Errors error={error} />;

  return (
    <Uploader
      className={className}
      awsPreSignedUrl={data.awsPreSignedUrl}
      file={file}
      preview={preview}
      onUploaded={onUploaded}
      onError={onError}
      ignoreUploadedState={ignoreUploadedState}
    />
  );
}

export default AWSUploader;
